/**
 * Iter121I — Entity Graph page
 *
 * Cross-case index of every subject / vehicle / location the caller can see,
 * with a lightweight relationship graph so investigators can spot the same
 * person / plate / address turning up across multiple cases.
 *
 * Route: /entity-graph
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Search, User, Car, MapPin, RefreshCw, ExternalLink, Users, Loader2,
  Network, Link2, X,
} from 'lucide-react';

const TYPE_ICON = { person: User, vehicle: Car, location: MapPin };
const TYPE_LABEL = { person: 'Person', vehicle: 'Vehicle', location: 'Location' };
const TYPE_COLOR = {
  person: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  vehicle: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
  location: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
};

export default function EntityGraphPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get('q') || '');
  const [typeFilter, setTypeFilter] = useState(params.get('type') || 'all');
  const [selected, setSelected] = useState(null);
  const [relationships, setRelationships] = useState([]);
  const [linkTargetId, setLinkTargetId] = useState('');
  const [linkType, setLinkType] = useState('ASSOCIATED_WITH');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (typeFilter !== 'all') qs.set('entity_type', typeFilter);
      const r = await fetch(`${BACKEND_URL}/api/entity-graph/entities?${qs.toString()}`, { headers: H });
      setEntities(r.ok ? await r.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [q, typeFilter, token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setRelationships([]); return; }
    (async () => {
      const r = await fetch(`${BACKEND_URL}/api/entity-graph/relationships?entity_id=${encodeURIComponent(selected.id)}`, { headers: H });
      setRelationships(r.ok ? await r.json() : []);
    })();
    // eslint-disable-next-line
  }, [selected?.id]);

  // Aggregate — same entity id across multiple cases means "shared entity"
  const byId = useMemo(() => {
    const m = new Map();
    for (const e of entities) {
      const key = `${e.entity_type}:${e.id}`;
      if (!m.has(key)) m.set(key, { ...e, appearances: [{ case_id: e.case_id, case_number: e.case_number, case_title: e.case_title }] });
      else m.get(key).appearances.push({ case_id: e.case_id, case_number: e.case_number, case_title: e.case_title });
    }
    return Array.from(m.values());
  }, [entities]);

  const filtered = byId;

  const linkEntities = async () => {
    if (!selected || !linkTargetId) return toast({ title: 'Pick target entity', variant: 'destructive' });
    const target = byId.find(e => e.id === linkTargetId);
    if (!target) return;
    const r = await fetch(`${BACKEND_URL}/api/entity-graph/relationships`, {
      method: 'POST', headers: JH,
      body: JSON.stringify({
        from_entity_id: selected.id, from_entity_type: selected.entity_type,
        to_entity_id: target.id, to_entity_type: target.entity_type,
        relationship_type: linkType,
      }),
    });
    const d = await r.json();
    if (!r.ok) return toast({ title: 'Link failed', description: d.detail || 'Error', variant: 'destructive' });
    toast({ title: 'Linked' });
    setLinkTargetId('');
    const rr = await fetch(`${BACKEND_URL}/api/entity-graph/relationships?entity_id=${encodeURIComponent(selected.id)}`, { headers: H });
    setRelationships(rr.ok ? await rr.json() : []);
  };

  const removeRel = async (relId) => {
    if (!window.confirm('Remove this relationship?')) return;
    const r = await fetch(`${BACKEND_URL}/api/entity-graph/relationships/${relId}`, { method: 'DELETE', headers: H });
    if (r.ok) setRelationships(rs => rs.filter(x => x.id !== relId));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4 pb-16" data-testid="entity-graph-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Network className="w-6 h-6 text-blue-400" />
            <h1 className="text-2xl font-bold text-white">Intelligence Graph</h1>
          </div>
          <p className="text-sm text-slate-400">Cross-case index of people, vehicles and locations. Click any entity to see every case it appears in and link it to another.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name / plate / address…"
              className="pl-9 bg-slate-800 border-slate-600 text-white w-72" data-testid="entity-graph-search" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="bg-slate-800 border-slate-600 text-white w-40" data-testid="entity-graph-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="person">People</SelectItem>
              <SelectItem value="vehicle">Vehicles</SelectItem>
              <SelectItem value="location">Locations</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="border-slate-600 !text-slate-200"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Entity list */}
        <div className="md:col-span-2 rounded-lg border border-slate-700 bg-[#131A2A]">
          <div className="px-4 py-2 border-b border-slate-700 text-sm text-white flex items-center gap-2">
            <Users className="w-4 h-4" /> {filtered.length} entities
            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          </div>
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No entities to show.</div>
          ) : (
            <div className="divide-y divide-slate-800 max-h-[70vh] overflow-y-auto">
              {filtered.map(e => {
                const Icon = TYPE_ICON[e.entity_type] || User;
                const isSelected = selected?.id === e.id;
                return (
                  <button key={`${e.entity_type}-${e.id}`} onClick={() => setSelected(e)}
                    className={`w-full text-left px-4 py-3 text-sm hover:bg-slate-800/40 ${isSelected ? 'bg-blue-900/30' : ''}`}
                    data-testid={`entity-row-${e.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-white font-medium flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="truncate">{e.label}</span>
                          {e.appearances.length > 1 && (
                            <Badge className="text-[10px] bg-amber-500/20 text-amber-300 border-amber-500/40 border">
                              {e.appearances.length}× shared
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                          <Badge variant="outline" className={`text-[10px] ${TYPE_COLOR[e.entity_type]}`}>{TYPE_LABEL[e.entity_type]}</Badge>
                          {e.appearances.map(a => (
                            <span key={a.case_id} className="text-slate-400">
                              {a.case_number || a.case_id?.slice(-6)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected entity details */}
        <div className="rounded-lg border border-slate-700 bg-[#131A2A]">
          {!selected ? (
            <div className="p-6 text-sm text-slate-500 text-center">Pick an entity to see its case appearances and linked relationships.</div>
          ) : (
            <>
              <div className="px-4 py-2 border-b border-slate-700 flex items-center justify-between">
                <div className="text-sm text-white font-semibold truncate flex items-center gap-2">
                  {(() => { const I = TYPE_ICON[selected.entity_type] || User; return <I className="w-4 h-4 text-slate-400" />; })()}
                  {selected.label}
                </div>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white" aria-label="Close"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-3 space-y-3">
                <div>
                  <div className="text-xs text-slate-400 uppercase mb-1">Appears In</div>
                  <div className="space-y-1">
                    {selected.appearances.map(a => (
                      <button key={a.case_id} onClick={() => navigate(`/cases/${a.case_id}`)}
                        className="w-full flex items-center justify-between rounded bg-slate-900 hover:bg-slate-800 px-3 py-1.5 text-xs">
                        <span className="text-white truncate">{a.case_number || a.case_id?.slice(-6)} — {a.case_title || 'Untitled'}</span>
                        <ExternalLink className="w-3 h-3 text-slate-400" />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase mb-1">Relationships ({relationships.length})</div>
                  {relationships.length === 0 ? (
                    <div className="text-xs text-slate-500 mb-2">None yet — link this entity to another below.</div>
                  ) : (
                    <div className="space-y-1 mb-2">
                      {relationships.map(r => {
                        const otherId = r.from_entity_id === selected.id ? r.to_entity_id : r.from_entity_id;
                        return (
                          <div key={r.id} className="flex items-center justify-between rounded bg-slate-900 px-3 py-1.5 text-xs">
                            <span className="text-white truncate">
                              <Link2 className="w-3 h-3 inline mr-1 text-slate-400" />
                              {r.relationship_type} → {otherId}
                            </span>
                            <button onClick={() => removeRel(r.id)} className="text-slate-400 hover:text-red-400"><X className="w-3 h-3" /></button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="space-y-1">
                    <Select value={linkTargetId} onValueChange={setLinkTargetId}>
                      <SelectTrigger className="bg-slate-800 border-slate-600 text-white text-xs" data-testid="entity-link-target-select">
                        <SelectValue placeholder="Link to another entity…" />
                      </SelectTrigger>
                      <SelectContent>
                        {byId.filter(e => e.id !== selected.id).slice(0, 100).map(e => (
                          <SelectItem key={e.id} value={e.id}>{TYPE_LABEL[e.entity_type]}: {e.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input value={linkType} onChange={e => setLinkType(e.target.value.toUpperCase())}
                      placeholder="Relationship (ASSOCIATED_WITH, OWNS, LIVES_AT…)"
                      className="bg-slate-800 border-slate-600 text-white text-xs" />
                    <Button className="bg-blue-600 hover:bg-blue-500 text-white w-full text-xs" onClick={linkEntities} data-testid="entity-link-submit-btn">
                      <Link2 className="w-3 h-3 mr-1" /> Link Entities
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
